// src/redux/eventsSlice.js
import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { logViewSession, logViewSessionsBatch } from "../services/eventsService";

const toStr = (v) => String(v ?? "").trim();
const lower = (v) => toStr(v).toLowerCase();

// Always fulfill (best-effort), keep payload shape consistent
export const logViewSessionAsync = createAsyncThunk(
  "events/logViewSession",
  async (payload = {}) => {
    try {
      const creatorId = toStr(payload.creatorId);
      if (!creatorId) return { creatorIds: [] };

      await logViewSession({
        creatorId,
        platform: lower(payload.platform) || "instagram",
        surface: lower(payload.surface) || "discover",
        durationMs: Number.isFinite(payload.durationMs) ? Number(payload.durationMs) : 0,
        startedAt: payload.startedAt,
        endedAt: payload.endedAt,
        unlocked: Boolean(payload.unlocked),
        city: payload.city,
        niche: payload.niche,
      });

      return { creatorIds: [creatorId] };
    } catch {
      // best-effort: don't break UX
      return { creatorIds: [] };
    }
  }
);

/**
 * Batch impressions (max 50) for IntersectionObserver usage.
 *
 * ✅ Accepts either:
 *   - an array of sessions (your current UI dispatches this)
 *   - OR { sessions: [...] } for flexibility
 */
export const logViewSessionsBatchAsync = createAsyncThunk(
  "events/logViewSessionsBatch",
  async (input) => {
    try {
      const sessions = Array.isArray(input) ? input : Array.isArray(input?.sessions) ? input.sessions : [];
      const safe = sessions.filter(Boolean);
      if (!safe.length) return { creatorIds: [] };

      await logViewSessionsBatch(safe);

      const creatorIds = safe
        .map((s) => (s?.creatorId ? toStr(s.creatorId) : ""))
        .filter(Boolean);

      return { creatorIds };
    } catch {
      // best-effort
      return { creatorIds: [] };
    }
  }
);

const initialState = {
  lastError: null,
  logging: false,
  loggedCreators: {}, // { [creatorId]: true }
};

const eventsSlice = createSlice({
  name: "events",
  initialState,
  reducers: {
    clearEventsError(state) {
      state.lastError = null;
    },
    clearLoggedCreators(state) {
      state.loggedCreators = {};
    },
  },
  extraReducers: (builder) => {
    builder
      // single
      .addCase(logViewSessionAsync.pending, (state) => {
        state.logging = true;
        state.lastError = null;
      })
      .addCase(logViewSessionAsync.fulfilled, (state, action) => {
        state.logging = false;
        state.lastError = null;
        const ids = action.payload?.creatorIds || [];
        for (const id of ids) state.loggedCreators[id] = true;
      })
      .addCase(logViewSessionAsync.rejected, (state, action) => {
        // Should be rare since we fulfill best-effort, but keep safe
        state.logging = false;
        state.lastError = action.payload || action.error?.message || "Failed to log view session.";
      })

      // batch
      .addCase(logViewSessionsBatchAsync.pending, (state) => {
        // keep lightweight; don't lock UI
        state.lastError = null;
      })
      .addCase(logViewSessionsBatchAsync.fulfilled, (state, action) => {
        const ids = action.payload?.creatorIds || [];
        for (const id of ids) state.loggedCreators[id] = true;
      })
      .addCase(logViewSessionsBatchAsync.rejected, (state, action) => {
        // best-effort: ignore in UX, but store if you want
        state.lastError = action.payload || action.error?.message || null;
      });
  },
});

export const { clearEventsError, clearLoggedCreators } = eventsSlice.actions;

export const selectEventsLogging = (s) => s.events?.logging || false;
export const selectEventsLastError = (s) => s.events?.lastError || null;
export const selectLoggedCreatorsMap = (s) => s.events?.loggedCreators || {};

export default eventsSlice.reducer;